import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/auth/AuthContext';
import { Router } from '@/router';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@/styles/theme.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: true, staleTime: 30_000 },
  },
});

const USE_MOCK =
  import.meta.env.VITE_USE_MOCK === '1' || import.meta.env.VITE_USE_MOCK === 'true';

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

/**
 * Dev-mode bootstrap (mirrors ASRPortal).
 *
 * Production (Phase 5): the PHP SPA shell (index.php) injects window.__CSRF__
 * and window.__USER__ before the bundle runs.
 *
 * Phase 1 mock dev: there's no shell. We seed a placeholder __CSRF__ so the
 * HTTP client has a token to send, and leave __USER__ null so the visitor
 * starts at /login.
 *
 * Phase 2 dev (real backend, VITE_USE_MOCK off): fetch /api/V1/Boot through the
 * Vite proxy to pick up the real CSRF token + current user, the way the shell
 * would in production.
 */
async function bootstrap(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (window.__CSRF__) return;

  if (USE_MOCK) {
    window.__CSRF__ = 'mock-csrf-token';
    window.__USER__ = window.__USER__ ?? null;
    return;
  }

  try {
    const res = await fetch('/api/V1/Boot', { credentials: 'include' });
    if (!res.ok) return;
    const data = (await res.json()) as { csrf?: string; user?: Window['__USER__'] };
    window.__CSRF__ = data.csrf ?? '';
    window.__USER__ = data.user ?? null;
  } catch {
    /* Boot unavailable — API calls will fail at the CSRF gate until backend is up. */
  }
}

bootstrap().then(() => {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
